import api from '@/lib/api'

// ── Domain ownership (backend #509) ───────────────────────────────────────────

export interface DomainOwnerUser {
  id: string
  name: string
  email: string
}

export interface MyDomain {
  id: string
  name: string
  domain: string
  logo_url?: string | null
  is_active: boolean
  /** UUID of the domain owner (always a domain admin). Null on legacy domains until first transfer. */
  owner_user_id: string | null
  owner: DomainOwnerUser | null
}

/** Fetch this domain's details including owner. Domain admins can access their own domain. */
export const getMyDomain = (domainId: string) =>
  api.get<{ success: true; data: MyDomain }>(`/domains/${domainId}`).then((r) => r.data.data)

/** Transfer domain ownership to another active domain admin. Only the current owner may call this. */
export const transferDomainOwnership = (domainId: string, userId: string) =>
  api.post<{ success: true; data: MyDomain }>(`/domains/${domainId}/transfer-ownership`, { user_id: userId }).then((r) => r.data.data)

export interface DomainSettings {
  timezone?: string
  date_format?: string
  distance_unit?: 'km' | 'mi' | 'miles'
  speed_unit?: 'kmh' | 'mph' | 'kmph'
  currency?: string
  map_provider?: 'google' | 'osm' | string
  idle_threshold_minutes?: number
  trip_min_distance_meters?: number
  overspeed_threshold_kmh?: number
  overspeed_threshold_kmph?: number
  unreachable_threshold_minutes?: number
  data_retention_days?: number
  theme?: Record<string, unknown>
  [key: string]: unknown
}

/**
 * Fetch the current tenant domain settings.
 * The `theme` sub-object is used by ThemeProvider to apply branding.
 */
export const getSettings = () =>
  api
    .get<{ success: true; data: DomainSettings }>('/settings')
    .then((r) => r.data.data)

/** Update domain settings (partial update). Pass `{ theme: {...} }` to update branding. */
export const updateSettings = (body: Partial<DomainSettings>) =>
  api
    .put<{ success: true; data: DomainSettings }>('/settings', body)
    .then((r) => r.data.data)

export interface PublicBranding {
  appName:           string | null
  logoUrl:           string | null
  logoDarkUrl:       string | null
  faviconUrl:        string | null
  primaryColor:      string | null
  primaryForeground: string | null
  borderRadius:      string | null
  fontFamily:        string | null
  locale:            string | null
  playStoreUrl:      string | null
  appStoreUrl:       string | null
}

/**
 * Public branding endpoint — no auth required (backend #637).
 * Resolved by Host header. Called on app mount so the login page
 * shows the correct domain name and logo before the user signs in.
 * Uses the same api instance (base URL) but the endpoint is public — no token needed.
 */
export const getPublicBranding = (): Promise<PublicBranding> =>
  api
    .get<{ success: true; data: PublicBranding }>('/domain/branding')
    .then((r) => r.data.data)
